import { format, isToday, isYesterday } from 'date-fns';
import { 
  Mail, Phone, MessageSquare, FileText, Paperclip, Check, CheckCheck, X, Reply, 
  Download, Play, Pause, Clock, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { Communication, ReplyContext } from './types';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

interface CommunicationTimelineProps {
  communications: Communication[];
  onRefresh?: () => void;
  onReply?: (replyContext: ReplyContext) => void;
}

interface CallBodyData {
  original_body?: string;
  recording_sid?: string;
  recording_url?: string;
  recording_duration?: number;
  duration_seconds?: number;
  transcript?: string;
  call_status?: string;
  child_call_sid?: string;
  type?: string;
}

type CallType = 'outgoing' | 'incoming' | 'missed';

const channelConfig = {
  email: { icon: Mail, label: 'Email', color: 'bg-primary/10 text-primary' },
  phone: { icon: Phone, label: 'Call', color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' },
  whatsapp: { icon: MessageSquare, label: 'WhatsApp', color: 'bg-green-100 dark:bg-green-900/30 text-green-600' },
  sms: { icon: MessageSquare, label: 'SMS', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' },
  note: { icon: FileText, label: 'Note', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' },
};

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatDateHeader(dateKey: string): string {
  // Parse yyyy-MM-dd as local date (not UTC) for correct today/yesterday comparison
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMM d');
}

function getCallTypeInfo(comm: Communication, callData: CallBodyData | null): { type: CallType; icon: typeof Phone; label: string; color: string } {
  const isOutbound = comm.direction === 'outbound';
  const status = callData?.call_status || comm.status;
  
  // Missed or failed calls
  if (status === 'no-answer' || status === 'busy' || status === 'failed' || status === 'canceled') {
    return {
      type: 'missed',
      icon: isOutbound ? PhoneOff : PhoneMissed,
      label: isOutbound ? 'No Answer' : 'Missed Call',
      color: 'text-destructive bg-destructive/10'
    };
  }
  
  // Completed calls
  if (isOutbound) {
    return {
      type: 'outgoing',
      icon: PhoneOutgoing,
      label: 'Outgoing Call',
      color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30'
    };
  }
  
  return {
    type: 'incoming',
    icon: PhoneIncoming,
    label: 'Incoming Call',
    color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30'
  };
}

function CallRecordingPlayer({ bodyData }: { bodyData: CallBodyData }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleTogglePlay = async () => {
    if (!bodyData.recording_url) return;

    if (audioRef) {
      if (isPlaying) {
        audioRef.pause();
        setIsPlaying(false);
      } else {
        audioRef.play();
        setIsPlaying(true);
      }
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('communication-attachments')
        .createSignedUrl(bodyData.recording_url, 3600);

      if (error) throw error;

      const audio = new Audio(data.signedUrl);
      audio.onended = () => setIsPlaying(false);
      audio.play();
      setAudioRef(audio);
      setIsPlaying(true);
    } catch (error) {
      console.error('Error loading recording:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!bodyData.recording_url) return;

    try {
      const { data, error } = await supabase.storage
        .from('communication-attachments')
        .download(bodyData.recording_url);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${bodyData.recording_sid}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading recording:', error);
    }
  };

  if (!bodyData.recording_url) return null;

  return (
    <div className="flex items-center gap-2 mt-2 p-2 rounded-md bg-muted/50">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-full"
        onClick={handleTogglePlay}
        disabled={isLoading}
      >
        {isLoading ? (
          <Clock className="h-4 w-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 ml-0.5" />
        )}
      </Button>
      <span className="text-xs text-muted-foreground flex-1">
        {isPlaying ? 'Playing...' : 'Play recording'}
      </span>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDownload}>
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function CommunicationTimeline({ communications, onRefresh, onReply }: CommunicationTimelineProps) {
  // Group by date
  const grouped = communications.reduce((acc, comm) => {
    const date = format(new Date(comm.created_at), 'yyyy-MM-dd');
    if (!acc[date]) acc[date] = [];
    acc[date].push(comm);
    return acc;
  }, {} as Record<string, Communication[]>);

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const handleReply = (comm: Communication) => {
    if (onReply && comm.channel === 'email') {
      onReply({
        originalMessageId: comm.id,
        subject: comm.subject?.startsWith('Re: ') ? comm.subject : `Re: ${comm.subject || ''}`,
        senderEmail: comm.direction === 'inbound' ? comm.sender_email || undefined : comm.recipient_email || undefined,
      });
    }
  };

  const downloadAttachment = async (storagePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('communication-attachments')
        .download(storagePath);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
    }
  };

  const parseCallBody = (comm: Communication): CallBodyData | null => {
    if (comm.channel !== 'phone') return null;
    try {
      return JSON.parse(comm.body) as CallBodyData;
    } catch {
      return null;
    }
  };

  return (
    <div className="divide-y">
      {sortedDates.map((dateKey) => (
        <div key={dateKey}>
          {/* Date Header */}
          <div className="sticky top-0 z-10 px-4 py-2 bg-muted/80 backdrop-blur-sm">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              {formatDateHeader(dateKey)}
            </span>
          </div>

          {/* Items */}
          <div className="divide-y divide-border/50">
            {grouped[dateKey].map((comm) => {
              const config = channelConfig[comm.channel] || channelConfig.email;
              const isOutbound = comm.direction === 'outbound';
              const callData = parseCallBody(comm);
              const isCall = comm.channel === 'phone';
              
              // Call-specific rendering
              if (isCall) {
                const callInfo = getCallTypeInfo(comm, callData);
                const CallIcon = callInfo.icon;
                
                return (
                  <div key={comm.id} className="px-4 py-3 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={cn('h-9 w-9 rounded-full flex items-center justify-center shrink-0', callInfo.color)}>
                        <CallIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{callInfo.label}</span>
                            {(callData?.recording_duration || callData?.duration_seconds) && (
                              <Badge variant="secondary" className="text-[10px] h-5">
                                {formatDuration(callData?.recording_duration || callData?.duration_seconds || 0)}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(comm.created_at), 'h:mm a')}
                          </span>
                        </div>
                        
                        {callData?.original_body && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {callData.original_body}
                          </p>
                        )}

                        {callData?.recording_url && <CallRecordingPlayer bodyData={callData} />}

                        {callData?.transcript && (
                          <Collapsible className="mt-2">
                            <CollapsibleTrigger className="text-xs text-primary hover:underline">
                              View transcript
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-2 p-2 rounded bg-muted/50 text-xs whitespace-pre-wrap">
                              {callData.transcript}
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // Regular communication rendering
              const Icon = config.icon;
              const statusIcons: Record<string, typeof Check | null> = {
                sent: Check,
                delivered: CheckCheck,
                failed: X,
                logged: Check,
              };
              const StatusIcon = statusIcons[comm.status] || null;

              return (
                <div key={comm.id} className="group px-4 py-3 hover:bg-muted/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={cn('h-9 w-9 rounded-full flex items-center justify-center shrink-0', config.color)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium truncate">
                            {isOutbound ? 'You' : (comm.sender_email || 'Contact')}
                          </span>
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {isOutbound ? 'Sent' : 'Received'}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {format(new Date(comm.created_at), 'h:mm a')}
                        </span>
                      </div>

                      {comm.subject && (
                        <p className="text-sm font-medium mt-0.5 truncate">{comm.subject}</p>
                      )}

                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{comm.body}</p>

                      {comm.attachments && comm.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {comm.attachments.map((att) => (
                            <button
                              key={att.id}
                              onClick={() => downloadAttachment(att.storage_path, att.file_name)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-muted hover:bg-accent"
                            >
                              <Paperclip className="h-3 w-3" />
                              <span className="max-w-20 truncate">{att.file_name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          {StatusIcon && (
                            <StatusIcon className={cn('h-3 w-3', comm.status === 'delivered' ? 'text-blue-500' : 'text-muted-foreground')} />
                          )}
                          <span className="text-[11px] text-muted-foreground capitalize">{comm.status}</span>
                        </div>
                        {comm.channel === 'email' && onReply && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs opacity-0 group-hover:opacity-100"
                            onClick={() => handleReply(comm)}
                          >
                            <Reply className="h-3 w-3 mr-1" />
                            Reply
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
